/**
 * Parses markdown content into React nodes with support for custom styles, classes, and various markdown features.
 *
 * @param markdown - The markdown string to parse.
 * @param options - Optional settings for parsing the markdown.
 * @returns An array of React nodes representing the parsed markdown content.
 *
 * @example
 * ```ts
 * const markdown = "# Hello World\nThis is a paragraph.";
 * const options = {
 *   customClasses: {
 *     headings: "my-heading-class",
 *     paragraphs: "my-paragraph-class",
 *   },
 *   customStyles: {
 *     headings: { color: "red" },
 *     paragraphs: { fontSize: "16px" },
 *   },
 * };
 * const parsedContent = parse(markdown, options);
 * ```
 *
 * @remarks
 * This code is open source under the MIT license. The author can be hired by visiting [Code Media Labs](https://codemedialabs.in).
 */

import React, { CSSProperties } from "react";

export interface CustomClasses {
  headings?: string;
  paragraphs?: string;
  lists?: string;
  blockquotes?: string;
  codeBlocks?: string;
  tables?: string;
  links?: string;
  images?: string;
  footnotes?: string;
  footnoteReference?: string;
  orderedLists?: string;
  unorderedLists?: string;
  listItems?: string;
}

export interface CustomStyles {
  headings?: CSSProperties;
  paragraphs?: CSSProperties;
  lists?: CSSProperties;
  blockquotes?: CSSProperties;
  codeBlocks?: CSSProperties;
  tables?: CSSProperties;
  links?: CSSProperties;
  images?: CSSProperties;
  footnotes?: CSSProperties;
  footnoteReference?: CSSProperties;
  orderedLists?: CSSProperties;
  unorderedLists?: CSSProperties;
  listItems?: CSSProperties;
}

/**
 * Custom components to override default markdown elements.
 */
export interface CustomComponents {
  h1?: React.ComponentType<any>;
  h2?: React.ComponentType<any>;
  h3?: React.ComponentType<any>;
  h4?: React.ComponentType<any>;
  h5?: React.ComponentType<any>;
  h6?: React.ComponentType<any>;
  p?: React.ComponentType<any>;
  ul?: React.ComponentType<any>;
  ol?: React.ComponentType<any>;
  li?: React.ComponentType<any>;
  dl?: React.ComponentType<any>;
  dt?: React.ComponentType<any>;
  dd?: React.ComponentType<any>;
  blockquote?: React.ComponentType<any>;
  code?: React.ComponentType<any>;
  pre?: React.ComponentType<any>;
  table?: React.ComponentType<any>;
  thead?: React.ComponentType<any>;
  tbody?: React.ComponentType<any>;
  tr?: React.ComponentType<any>;
  th?: React.ComponentType<any>;
  td?: React.ComponentType<any>;
  a?: React.ComponentType<any>;
  img?: React.ComponentType<any>;
  del?: React.ComponentType<any>;
  sup?: React.ComponentType<any>;
  sub?: React.ComponentType<any>;
  mark?: React.ComponentType<any>;
  strong?: React.ComponentType<any>;
  em?: React.ComponentType<any>;
  math?: React.ComponentType<{ content: string; isBlock: boolean }>;
  alert?: React.ComponentType<{ type: string; children: React.ReactNode }>;
  footnoteReference?: React.ComponentType<{ id: string; label: string }>;
  footnoteDefinition?: React.ComponentType<{ id: string; children: React.ReactNode }>;
}

/**
 * Options for parsing markdown content.
 */
export interface ParseOptions {
  /**
   * Prefix for language-specific code block classes.
   * @example "language-"
   */
  langPrefix?: string;

  /**
   * Custom CSS classes for different markdown elements.
   */
  customClasses?: CustomClasses;

  /**
   * Custom CSS styles for different markdown elements.
   */
  customStyles?: CustomStyles;

  /**
   * Target attribute for links.
   * @default "_blank"
   */
  linkTarget?: "_blank" | "_self" | "_parent" | "_top";

  /**
   * Whether to sanitize HTML content.
   * @default false
   */
  sanitizeHtml?: boolean;

  /**
   * Maximum allowed nesting level for lists.
   * @default 6
   */
  maxNestingLevel?: number;

  /**
   * Custom components to override default markdown elements.
   */
  components?: CustomComponents;

  /**
   * Callback for rendering math content.
   * If provided, this function will be called for both inline ($) and block ($$) math.
   * @param content - The raw math content (TeX/LaTeX).
   * @param isBlock - Whether the math is a block-level element ($$).
   */
  onRenderMath?: (content: string, isBlock: boolean) => React.ReactNode;

  /**
   * Callback for custom syntax highlighting.
   * If provided, this function will be called for all code blocks (```).
   * @param code - The raw code content.
   * @param language - The language identifier (e.g. "javascript", "python").
   */
  onRenderCode?: (code: string, language?: string) => React.ReactNode;
}

type ElementType =
  | "headings"
  | "paragraphs"
  | "lists"
  | "blockquotes"
  | "codeBlocks"
  | "tables"
  | "links"
  | "images"
  | "footnotes"
  | "footnoteReference"
  | "orderedLists"
  | "unorderedLists"
  | "listItems";

/**
 * Modern token-based markdown parser for React.
 * 
 * This function performs a two-pass scan on the input markdown:
 * 1. Scans for footnote definitions (`[^1]:`) and hoists them.
 * 2. Parses the content into a tree of React components without using `dangerouslySetInnerHTML`.
 *
 * Support is included for:
 * - GitHub Alerts (`> [!NOTE]`)
 * - Footnotes (`[^1]`)
 * - Math (inline and block)
 * - Tables, Code Blocks, Task Lists, and more.
 *
 * @param markdown - The raw markdown string.
 * @param options - Configuration for styling and component overrides.
 * @returns An array of uniquely keyed React fragments/nodes.
 */
export function parse(
  markdown: string,
  options: ParseOptions = {},
  depth = 0
): React.ReactNode[] {
  const {
    langPrefix = "language-",
    customClasses = {},
    customStyles = {},
    linkTarget = "_blank",
    sanitizeHtml = true,
    maxNestingLevel = 6,
  } = options;

  const lines = markdown.split(/\r?\n/);
  const html: React.ReactNode[] = [];

  if (depth >= maxNestingLevel) {
    return [<p key="max-nest">Maximum nesting level reached</p>];
  }
  let inCodeBlock = false;
  let codeBlock: string[] = [];
  let codeBlockLang: string | null = null;
  let inMathBlock = false;
  let mathBlock: string[] = [];
  let nestingLevel = 0;
  let skipLines = 0;

  const getComponent = (tag: keyof CustomComponents, defaultTag: any) =>
    options.components?.[tag] || defaultTag;

  const getElementProperties = (
    elementType: ElementType,
    additionalProps: Record<string, any> = {}
  ): { style?: CSSProperties; className?: string } => {
    const style: CSSProperties = {
      ...customStyles[elementType],
      ...additionalProps.style,
    };
    const className = customClasses[elementType]
      ? `${customClasses[elementType]}${
          additionalProps.className ? ` ${additionalProps.className}` : ""
        }`
      : additionalProps.className;
    return {
      ...additionalProps,
      style: Object.keys(style).length > 0 ? style : undefined,
      className: className || undefined,
    };
  };

  // Enhanced sanitization function
  // const sanitize = (text: string): string => {
  //   if (!sanitizeHtml) return text;
  //   return text
  //     .replace(/&/g, "&amp;")
  //     .replace(/</g, "&lt;")
  //     .replace(/>/g, "&gt;")
  //     .replace(/"/g, "&quot;")
  //     .replace(/'/g, "&#039;");
  // };

  /**
   * Process inline formatting into React nodes.
   * This handles bold, italic, links, images, math, and more.
   */
  const processInlineFormatting = (text: string): React.ReactNode[] => {
    if (!text) return [];

    const tokenize = (input: string): React.ReactNode[] => {
      if (!input) return [];
      let earliestMatch: any = null;
      let earliestRule: any = null;

      // Rule definitions: consolidated to save space
      const rules = [
        {
          regex: /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)/,
          render: (m: string, alt: string, src: string, title?: string) => {
            const Img = getComponent("img", "img");
            return <Img key={m} {...getElementProperties("images", { src, alt: alt || "", title })} />;
          },
        },
        {
          regex: /\[([^\]]+)\]\(([^)"'\s]+)(?:\s+"([^"]+)")?\)/,
          render: (m: string, label: string, url: string, title?: string) => {
            const A = getComponent("a", "a");
            return (
              <A key={m} {...getElementProperties("links", { href: url, title, target: linkTarget, rel: linkTarget === "_blank" ? "noopener noreferrer" : undefined })}>
                {tokenize(label)}
              </A>
            );
          },
        },
        {
          regex: /\$?\$\$\s*([\s\S]+?)\s*\$\$\$?/,
          render: (m: string, content: string) => {
            if (options.onRenderMath) return <React.Fragment key={m}>{options.onRenderMath(content, true)}</React.Fragment>;
            const MathComp = getComponent("math", "div");
            const props = getElementProperties("paragraphs", { className: "math-block", style: { textAlign: "center", margin: "1rem 0" } });
            const isCustom = typeof MathComp !== "string";
            return (
              <MathComp key={m} {...props} {...(isCustom ? { content, isBlock: true } : {})}>
                {content}
              </MathComp>
            );
          },
        },
        {
          regex: /\$([^$\n]+)\$/,
          render: (m: string, content: string) => {
            if (options.onRenderMath) return <React.Fragment key={m}>{options.onRenderMath(content, false)}</React.Fragment>;
            const MathComp = getComponent("math", "span");
            const props = getElementProperties("paragraphs", { className: "math-inline" });
            const isCustom = typeof MathComp !== "string";
            return (
              <MathComp key={m} {...props} {...(isCustom ? { content, isBlock: false } : {})}>
                {content}
              </MathComp>
            );
          },
        },
        // Simple formatting rules using a shorter notation
        { regex: /~~(.+?)~~/, tag: "del" },
        { regex: /\^(.+?)\^/, tag: "sup" },
        { regex: /~(.+?)~/, tag: "sub" },
        { regex: /==(.+?)==/, tag: "mark" },
        { regex: /\*\*\*(.+?)\*\*\*/, render: (m: string, c: string) => <strong key={m}><em>{tokenize(c)}</em></strong> },
        { regex: /\*\*(.+?)\*\*/, tag: "strong" },
        { regex: /__(.+?)__/, tag: "strong" },
        { regex: /\*(.+?)\*/, tag: "em" },
        { regex: /_(.+?)_/, tag: "em" },
        { regex: /`([^`]+)`/, render: (m: string, c: string) => { const Code = getComponent("code", "code"); return <Code key={m}>{c}</Code>; } },
        {
          regex: /\[\^([^\]]+)\]/,
          render: (m: string, label: string) => {
            const FootRef = getComponent("footnoteReference", "sup");
            return <FootRef key={m} {...getElementProperties("footnoteReference", { id: `fnref-${label}`, className: "footnote-ref" })}><a href={`#fn-${label}`}>{label}</a></FootRef>;
          },
        },
      ];

      for (const rule of rules) {
        const match = rule.regex.exec(input);
        if (match && (!earliestMatch || match.index < earliestMatch.index)) {
          earliestMatch = match;
          earliestRule = rule;
        }
      }

      if (earliestMatch && earliestRule) {
        const before = input.slice(0, earliestMatch.index);
        const after = input.slice(earliestMatch.index + earliestMatch[0].length);
        const matchContent = earliestRule.render 
          ? earliestRule.render(...earliestMatch) 
          : React.createElement(getComponent(earliestRule.tag as any, earliestRule.tag), { key: earliestMatch[0] }, tokenize(earliestMatch[1]));

        return [before, matchContent, ...tokenize(after)].filter(Boolean);
      }
      return [input];
    };

    return tokenize(text);
  };

  // First pass: scan for footnote definitions
  const footnotes: Record<string, React.ReactNode[]> = {};
  const processedLines: string[] = [];
  lines.forEach((line) => {
    const fnMatch = line.match(/^\[\^([^\]]+)\]:\s*(.*)$/);
    if (fnMatch) footnotes[fnMatch[1]] = processInlineFormatting(fnMatch[2]);
    else processedLines.push(line);
  });

  // Process each line
  for (let i = 0; i < processedLines.length; i++) {
    const line = processedLines[i];
    const index = i;

    if (skipLines > 0) {
      skipLines--;
      continue;
    }
    // Calculate nesting level for lists/tasks to check before rendering
    let currentNestingLevel = 0;
    const isListItem = /^(\s*)([-*+]|\d+\.)\s/.test(line);
    const isTaskItem = /^(\s*)-\s\[(x| )\]/.test(line.trim());
    
    if (isListItem || isTaskItem) {
      const indent = (line.match(/^(\s*)/)?.[0] || "").length;
      currentNestingLevel = Math.floor(indent / 2);
    }
    
    if (currentNestingLevel > maxNestingLevel) {
      html.push(<p key={`error-${index}`}>Maximum nesting level reached</p>);
      continue;
    }
    
    nestingLevel = currentNestingLevel;

    // Handle definition lists - needs multiple line lookahead
    if (i < processedLines.length - 1) {
      const currentLine = line.trim();
      const nextLine = processedLines[i + 1].trim();

      if (
        currentLine !== "" &&
        !currentLine.startsWith(":") &&
        nextLine.startsWith(":")
      ) {
        const term = currentLine;
        const definitions: string[] = [];
        let j = i + 1;

        // Collect all subsequent definitions
        while (j < processedLines.length && processedLines[j].trim().startsWith(":")) {
          definitions.push(processedLines[j].trim().slice(1).trim());
          j++;
        }

        // Set the number of lines to skip
        skipLines = definitions.length;

        // Create the definition list element
        const Dl = getComponent("dl", "dl");
        const Dt = getComponent("dt", "dt");
        const Dd = getComponent("dd", "dd");
        html.push(
          <Dl key={`dl-${i}`} {...getElementProperties("lists")}>
            <Dt>{processInlineFormatting(term)}</Dt>
            {definitions.map((def, defIndex) => (
              <Dd key={defIndex}>{processInlineFormatting(def)}</Dd>
            ))}
          </Dl>
        );

        continue;
      }
    }

    // Headers with custom IDs
    if (/^#{1,6}\s/.test(line.trim())) {
      const level = line.trim().match(/^#{1,6}/)![0].length;
      const text = line.replace(/^#{1,6}\s/, "");
      const idMatch = text.match(/\{#([^}]+)\}/);
      const id = idMatch ? idMatch[1] : undefined;
      const cleanText = text.replace(/\{#([^}]+)\}/, "").trim();

      const Header = getComponent(`h${level}` as keyof CustomComponents, `h${level}`);
      html.push(
        <Header
          key={`h-${i}`}
          id={id}
          {...getElementProperties("headings")}
        >
          {processInlineFormatting(cleanText)}
        </Header>
      );
      continue;
    }

    // Single-line math block ($$ ... $$)
    if (/^\$\$\s*(.+)\s*\$\$$/.test(line.trim())) {
      const match = line.trim().match(/^\$\$\s*(.+)\s*\$\$$/);
      const content = (match?.[1] || "").trim();
      if (options.onRenderMath) {
        html.push(
          <React.Fragment key={`math-inline-block-${i}`}>
            {options.onRenderMath(content, true)}
          </React.Fragment>
        );
      } else {
        const MathComp = getComponent("math", "div");
        const props = getElementProperties("paragraphs", {
          className: "math-block",
          style: { textAlign: "center", margin: "1rem 0" },
        });
        const isCustom = typeof MathComp !== "string";
        html.push(
          <MathComp
            key={`math-inline-block-${i}`}
            {...(isCustom ? { content, isBlock: true } : {})}
            {...props}
          >
            {content}
          </MathComp>
        );
      }
      continue;
    }

    // Math blocks (multiline)
    if (line.trim() === "$$") {
      inMathBlock = !inMathBlock;
      if (inMathBlock) {
        mathBlock = [];
      } else {
        const content = mathBlock.join("\n");
        if (options.onRenderMath) {
          html.push(
            <React.Fragment key={`math-block-${i}`}>
              {options.onRenderMath(content, true)}
            </React.Fragment>
          );
        } else {
          const MathComp = getComponent("math", "div");
          const props = getElementProperties("paragraphs", {
            className: "math-block",
            style: { textAlign: "center", margin: "1rem 0" },
          });
          const isCustom = typeof MathComp !== "string";
          html.push(
            <MathComp
              key={`math-block-${i}`}
              {...(isCustom ? { content, isBlock: true } : {})}
              {...props}
            >
              {content}
            </MathComp>
          );
        }
        mathBlock = [];
      }
      continue;
    }

    if (inMathBlock) {
      mathBlock.push(line);
      continue;
    }

    // Code blocks
    if (/^```(\S+)?(\s+\{.*\})?$/.test(line.trim())) {
      inCodeBlock = !inCodeBlock;
      if (inCodeBlock) {
        const match = line.trim().match(/^```(\S+)?/);
        codeBlockLang = match ? (match[1] || null) : null;
        codeBlock = [];
      } else {
        const code = codeBlock.join("\n");
        if (options.onRenderCode) {
          html.push(
            <React.Fragment key={`code-${i}`}>
              {options.onRenderCode(code, codeBlockLang || undefined)}
            </React.Fragment>
          );
        } else {
          const Pre = getComponent("pre", "pre");
          const Code = getComponent("code", "code");
          html.push(
            <Pre key={`code-${i}`} {...getElementProperties("codeBlocks")}>
              <Code
                className={
                  codeBlockLang ? `${langPrefix}${codeBlockLang}` : undefined
                }
              >
                {code}
              </Code>
            </Pre>
          );
        }
        codeBlock = [];
        codeBlockLang = null;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlock.push(line);
      continue;
    }

    if (/^>\s/.test(line.trim())) {
      let j = i;
      const bqLines = [];
      let alertType: string | null = null;
      let firstLineContent: string | null = null;

      while (j < processedLines.length) {
        const currLine = processedLines[j];
        if (!currLine || !/^>\s?/.test(currLine.trim())) break;

        const stripped = currLine.trim().replace(/^>\s?/, "");
        
        // Check for alert on the FIRST line of the block
        if (j === i) {
          const alertMatch = stripped.match(/^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*(.*)$/i);
          if (alertMatch) {
            alertType = alertMatch[1].toUpperCase();
            firstLineContent = alertMatch[2];
            if (firstLineContent.trim()) {
              bqLines.push(firstLineContent);
            }
          } else {
            bqLines.push(stripped);
          }
        } else {
          bqLines.push(stripped);
        }
        j++;
      }

      const content = parse(bqLines.join("\n"), options, depth + 1);
      
      if (alertType) {
        const AlertComp = getComponent("alert", "div");
        html.push(
          <AlertComp
            key={`alert-${i}`}
            type={alertType}
            className={`markdown-alert markdown-alert-${alertType.toLowerCase()}`}
          >
            {content}
          </AlertComp>
        );
      } else {
        // Check for citation in the last line (optional, but keep compatibility)
        let footer: React.ReactNode = null;
        const lastLineIdx = bqLines.length - 1;
        const citationMatch = bqLines[lastLineIdx]?.match(/^(.+?)(?:\s+--\s+(.+))$/);
        
        let parsedContent = content;
        if (citationMatch) {
          const [, text, author] = citationMatch;
          const linesWithoutCitation = [...bqLines.slice(0, lastLineIdx), text];
          parsedContent = parse(linesWithoutCitation.join("\n"), options, depth + 1);
          footer = (
            <footer
              style={{
                marginTop: "0.5rem",
                fontSize: "0.9em",
                textAlign: "right",
              }}
            >
              &mdash; {author}
            </footer>
          );
        }

        const Blockquote = getComponent("blockquote", "blockquote");
        html.push(
          <Blockquote key={`bq-${i}`} {...getElementProperties("blockquotes")}>
            {parsedContent}
            {footer}
          </Blockquote>
        );
      }
      i = j - 1;
      continue;
    }

    // Tables with alignment
    if (/^\|(.+\|)+/.test(line.trim())) {
      // Check if this is not an alignment row
      if (!/^\|(\s*:?-+:?\s*\|)+/.test(line.trim())) {
        const tableRows: string[] = [line.trim()];
        const alignments: Array<"left" | "center" | "right"> = [];

        // Check for alignment row
        if (
          i + 1 < processedLines.length &&
          /^\|(\s*:?-+:?\s*\|)+/.test(processedLines[i + 1].trim())
        ) {
          const alignmentRow = processedLines[i + 1].trim();
          alignments.push(
            ...alignmentRow
              .split("|")
              .slice(1, -1)
              .map((cell) => {
                if (cell.trim().startsWith(":") && cell.trim().endsWith(":"))
                  return "center";
                if (cell.trim().endsWith(":")) return "right";
                return "left";
              })
          );
          i++; // Skip alignment row
        }

        // Collect body rows
        while (
          i + 1 < processedLines.length &&
          /^\|(.+\|)+/.test(processedLines[i + 1].trim()) &&
          !/^\|(\s*:?-+:?\s*\|)+/.test(processedLines[i + 1].trim())
        ) {
          tableRows.push(processedLines[i + 1].trim());
          i++;
        }

        const headerColumns = tableRows[0]
          .split("|")
          .slice(1, -1)
          .map((col) => col.trim());

        const tableProps = getElementProperties("tables");
        const Table = getComponent("table", "table");
        const Thead = getComponent("thead", "thead");
        const Tbody = getComponent("tbody", "tbody");
        const Tr = getComponent("tr", "tr");
        const Th = getComponent("th", "th");
        const Td = getComponent("td", "td");

        html.push(
          <Table key={`table-${i}`} {...tableProps}>
            <Thead>
              <Tr>
                {headerColumns.map((col, idx) => (
                  <Th key={idx} style={{ textAlign: alignments[idx] || "left" }}>
                    {processInlineFormatting(col)}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {tableRows.slice(1).map((row, rowIndex) => (
                <Tr key={rowIndex}>
                  {row
                    .split("|")
                    .slice(1, -1)
                    .map((cell, cellIndex) => (
                      <Td
                        key={cellIndex}
                        style={{ textAlign: alignments[cellIndex] || "left" }}
                      >
                        {processInlineFormatting(cell.trim())}
                      </Td>
                    ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        );
        continue;
      }
    }

    // Images
    if (/^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)$/.test(line.trim())) {
      const match = line.trim().match(/^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)$/);
      const [, alt = "", src = "", title = ""] = match!;
      html.push(
        <img
          key={`img-${i}`}
          src={src}
          alt={alt}
          title={title || undefined}
          {...getElementProperties("images")}
        />
      );
      continue;
    }

    // Lists (ordered and unordered)
    if (/^(\s*)([-*+]|\d+\.)\s/.test(line)) {
      const match = line.match(/^\s*/);
      const currentIndent = match![0].length;
      const isOrdered = /^\s*\d+\./.test(line);
      const ListTag = isOrdered ? "ol" : "ul";
      const ListComp = getComponent(ListTag, ListTag);
      const Li = getComponent("li", "li");

      const items: { contentLines: string[]; isTask: boolean; checked: boolean }[] = [];
      let j = i;

      while (j < processedLines.length) {
        const currLine = processedLines[j];
        if (!currLine) {
          j++;
          continue;
        }

        const match = currLine.match(/^\s*/);
        const currIndent = match![0].length;
        const currTrim = currLine.trim();
        const currIsListItem = /^([-*+]|\d+\.)(\s+|$)/.test(currTrim);
        const currIsOrdered = /^\d+\./.test(currTrim);

        // If it's a new item at the SAME level
        if (currIsListItem && currIndent === currentIndent && currIsOrdered === isOrdered) {
          const taskMatch = currTrim.match(/^([-*+]|\d+\.)\s+\[([ xX])\](?:\s+(.*))?$/);
          items.push({
            isTask: !!taskMatch,
            checked: taskMatch ? taskMatch[2].toLowerCase() === "x" : false,
            contentLines: [taskMatch ? taskMatch[3] : currTrim.replace(/^([-*+]|\d+\.)\s+/, "")],
          });
          j++;
        } 
        // If it's indented more or it's an empty line within the list
        else if (currIndent > currentIndent || currTrim === "") {
          const content = currLine.slice(currentIndent);
          items[items.length - 1].contentLines.push(content);
          j++;
        } 
        // Otherwise, it's the end of the list
        else {
          break;
        }
      }

      html.push(
        <ListComp key={`list-${i}`} {...getElementProperties(isOrdered ? "orderedLists" : "unorderedLists")}>
          {items.map((item, idx) => {
            // Trim common leading spaces from contentLines to avoid over-indentation in recursive parse
            const trimmedLines = item.contentLines.map(l => l.replace(/^\s{1,4}/, ""));
            const content = parse(trimmedLines.join("\n"), options, depth + 1);
            
            if (item.isTask) {
              return (
                <Li
                  key={`li-${idx}`}
                  {...getElementProperties("listItems", {
                    className: "task-list-item",
                    style: {
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      listStyleType: "none",
                      marginLeft: "-1.2rem",
                    },
                  })}
                >
                  <input
                    type="checkbox"
                    checked={item.checked}
                    readOnly
                    style={{
                      marginTop: "0.4rem",
                      cursor: "default",
                    }}
                  />
                  <div style={{ flex: 1 }}>{content}</div>
                </Li>
              );
            }

            return (
              <Li key={`li-${idx}`} {...getElementProperties("listItems")}>
                {content}
              </Li>
            );
          })}
        </ListComp>
      );

      i = j - 1;
      continue;
    }

    // Process regular paragraphs with inline formatting
    const processedNodes = processInlineFormatting(line.trim());
    if (processedNodes.length > 0) {
      const P = getComponent("p", "p");
      html.push(
        <P key={`p-${i}`} {...getElementProperties("paragraphs")}>
          {processedNodes}
        </P>
      );
    }
  }

  // Final pass: append footnotes section if any were used
  const usedFootnotes = Object.keys(footnotes);
  if (usedFootnotes.length > 0) {
    const Section = getComponent("dl", "section"); // Default to section
    const FootDef = getComponent("footnoteDefinition", "div");
    html.push(
      <Section key="footnotes-section" {...getElementProperties("footnotes", { className: "footnotes" })}>
        <hr />
        {usedFootnotes.map((id) => (
          <FootDef
            key={id}
            {...getElementProperties("footnotes", {
              id: `fn-${id}`,
              className: "footnote-item",
            })}
          >
            <span className="footnote-label">[{id}]: </span>
            {footnotes[id]}
          </FootDef>
        ))}
      </Section>
    );
  }

  return html;
}
